import { useMutation, useQuery } from "@tanstack/react-query";
import {
  CheckIcon,
  Edit,
  MoreVertical,
  Notebook,
  Pencil,
  Plus,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import Page from "@/components/Common/Page";
import { CardListSkeleton } from "@/components/Common/SkeletonLoading";

import useFilters from "@/hooks/useFilters";

import query from "@/Utils/request/query";
import useAuthUser from "@/hooks/useAuthUser";
import { SchedulableResourceType } from "@/types/scheduling/schedule";
import scheduleApi from "@/types/scheduling/scheduleApi";
import { TokenQueueRead } from "@/types/tokens/tokenQueue/tokenQueue";
import tokenQueueApi from "@/types/tokens/tokenQueue/tokenQueueApi";
import { TokenSubQueueRead } from "@/types/tokens/tokenSubQueue/tokenSubQueue";
import tokenSubQueueApi from "@/types/tokens/tokenSubQueue/tokenSubQueueApi";
import { UserReadMinimal } from "@/types/user/user";

import CareIcon from "@/CAREUI/icons/CareIcon";
import mutate from "@/Utils/request/mutate";
import queryClient from "@/Utils/request/queryClient";
import { dateQueryString, formatName } from "@/Utils/utils";
import { TooltipComponent } from "@/components/ui/tooltip";
import { FolderOpenIcon } from "@heroicons/react/24/outline";
import { startOfDay } from "date-fns";
import dayjs from "dayjs";
import { Link } from "raviger";
import ManageServicePoints from "./ManageServicePointSheet";
import QueueFormSheet from "./QueueFormSheet";
import SubQueueFormSheet from "./SubQueueFormSheet";

interface QueueRowProps {
  queue: TokenQueueRead;
  facilityId: string;
  resourceType: SchedulableResourceType;
  resourceId: string;
  index: number;
}

function QueueRow({
  queue,
  facilityId,
  resourceType,
  resourceId,
  index,
}: QueueRowProps) {
  const { t } = useTranslation();
  const queueLink =
    resourceType === SchedulableResourceType.Practitioner
      ? `/facility/${facilityId}/practitioner/${resourceId}/queues/${queue.id}/ongoing`
      : `/queues/${queue.id}/ongoing`;

  const { mutate: setPrimary } = useMutation({
    mutationFn: mutate(tokenQueueApi.setPrimary, {
      pathParams: { facility_id: facilityId, id: queue.id },
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["tokenQueues", facilityId],
      });
    },
  });

  return (
    <TableRow className="hover:bg-gray-200">
      <TableCell className="font-medium text-gray-700 w-12 border-r border-gray-200 bg-white">
        {index + 1}
      </TableCell>
      <TableCell className="border-r border-gray-200 bg-white">
        <div className="flex items-center gap-2 justify-between">
          <div className="flex items-center gap-2">
            <Link href={queueLink} className="font-medium underline">
              {queue.name === "System Generated"
                ? t("primary_queue")
                : queue.name}
            </Link>
            {queue.is_primary && (
              <Badge
                variant="secondary"
                className="text-xs bg-purple-100 text-purple-800"
              >
                {t("primary")}
              </Badge>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-3 text-xs border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            asChild
          >
            <Link href={queueLink}>{t("open")}</Link>
          </Button>
        </div>
      </TableCell>
      <TableCell className="border-r border-gray-200 bg-white">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-3 text-xs bg-green-100 text-green-700 border-green-200 hover:bg-green-200"
          >
            {t("active")}
          </Button>
        </div>
      </TableCell>
      <TableCell className="w-12 border-r border-gray-200 bg-white rounded-r-md">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreVertical className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="border border-gray-200">
            <DropdownMenuItem asChild>
              <Link href={queueLink} className="flex items-center gap-2">
                <Notebook className="size-4 mr-2" />
                {t("open_queue_board")}
              </Link>
            </DropdownMenuItem>
            <QueueFormSheet
              facilityId={facilityId}
              resourceType={resourceType}
              resourceId={resourceId}
              queueId={queue.id}
              trigger={
                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                  <Pencil className="size-4 mr-2" />
                  {t("edit_queue_name")}
                </DropdownMenuItem>
              }
            />
            <DropdownMenuItem
              onClick={() => setPrimary({})}
              disabled={queue.is_primary}
            >
              <CheckIcon className="size-4 mr-2" />
              {t("set_as_primary")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

interface SubQueueCardProps {
  facilityId: string;
  subQueue: TokenSubQueueRead;
  showEditForAllCards: boolean;
}

function SubQueueCard({
  facilityId,
  subQueue,
  showEditForAllCards,
}: SubQueueCardProps) {
  return (
    <ManageServicePoints
      facilityId={facilityId}
      subQueue={subQueue}
      showEditOptionForEachCard={showEditForAllCards}
    ></ManageServicePoints>
  );
}

export default function QueuesIndex({
  facilityId,
  resourceType,
  resourceId,
}: {
  facilityId: string;
  resourceType: SchedulableResourceType;
  resourceId?: string;
}) {
  const { t } = useTranslation();
  const { qParams, updateQuery, Pagination, resultsPerPage } = useFilters({
    disableCache: true,
  });

  const { id: currentUserId } = useAuthUser();

  // Set default resourceId for practitioners
  const effectiveResourceId =
    qParams.resource_id ||
    resourceId ||
    (resourceType === SchedulableResourceType.Practitioner
      ? currentUserId.toString()
      : undefined);

  // Fetch available users for practitioner resource type
  const { data: availableUsersData } = useQuery({
    queryKey: ["availableUsers", facilityId],
    queryFn: query(scheduleApi.appointments.availableUsers, {
      pathParams: { facilityId },
    }),
    enabled: resourceType === SchedulableResourceType.Practitioner,
  });

  const availableUsers = availableUsersData?.users || [];

  // Set default date to today if no date is specified
  useEffect(() => {
    if (!qParams.date) {
      const today = new Date();
      updateQuery({ date: dateQueryString(today) });
    }
  }, [qParams.date, updateQuery]);

  // Handle date filter
  const handleDateChange = (date: Date | undefined) => {
    if (date) {
      updateQuery({ date: dateQueryString(date) });
    } else {
      updateQuery({ date: undefined });
    }
  };

  // Handle resource selection
  const handleResourceChange = (selectedResourceId: string) => {
    updateQuery({ resource_id: selectedResourceId });
  };

  // Fetch queues
  const { data: queuesResponse, isLoading: queuesLoading } = useQuery({
    queryKey: ["tokenQueues", facilityId, effectiveResourceId, qParams],
    queryFn: query(tokenQueueApi.list, {
      pathParams: { facility_id: facilityId },
      queryParams: {
        resource_type: resourceType,
        resource_id: effectiveResourceId,
        date: qParams.date,
        limit: resultsPerPage,
        offset: ((qParams.page ?? 1) - 1) * resultsPerPage,
      },
    }),
  });

  // Fetch sub-queues (service points)
  const { data: subQueuesResponse, isLoading: subQueuesLoading } = useQuery({
    queryKey: ["tokenSubQueues", facilityId, effectiveResourceId],
    queryFn: query(tokenSubQueueApi.list, {
      pathParams: { facility_id: facilityId },
      queryParams: {
        resource_type: resourceType,
        resource_id: effectiveResourceId,
      },
    }),
  });

  const queues = queuesResponse?.results || [];
  const subQueues = subQueuesResponse?.results || [];
  const isPast = dayjs(qParams.date).isBefore(dayjs(), "day");
  const [showEdit, setShowEdit] = useState(false);

  return (
    <Page title={t("token_queues")} hideTitleOnPage>
      <div className="container mx-auto px-4 py-1">
        {/* AayushNet Logo - Top right with proper spacing */}
        <div className="mb-4 flex items-center gap-0 justify-end">
          <img
            src="/images/aayushnet_logo_light.png"
            className="h-12"
            alt="AayushNet logo"
          />
          <h1
            className="text-2xl font-black tracking-wider"
            style={{
              fontFamily: "'Zen Kaku Gothic Antique', sans-serif",
            }}
          >
            <span
              style={{
                background:
                  "linear-gradient(to bottom, #5ba3ff 0%, #002d6b 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                display: "inline-block",
                paddingBottom: "2px",
              }}
            >
              Aayush
            </span>
            <span
              style={{
                background:
                  "linear-gradient(to bottom, #66ff99 0%, #1a7a3f 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                display: "inline-block",
                paddingBottom: "2px",
              }}
            >
              Net
            </span>
          </h1>
        </div>

        {/* Header Section - Date, Practitioner, Create Queue */}
        <div className="mb-8 flex flex-wrap gap-4 items-end bg-white p-4 rounded-lg border border-gray-200">
          {/* Date Filter */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              {t("date")}
            </label>
            <DatePicker
              date={qParams.date ? new Date(qParams.date) : undefined}
              onChange={handleDateChange}
            />
          </div>

          {/* Resource Picker - Only show for Practitioner resource type */}
          {resourceType === SchedulableResourceType.Practitioner && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                {t("selected_practitioner")}
              </label>
              <Select
                value={qParams.resource_id || effectiveResourceId}
                onValueChange={handleResourceChange}
              >
                <SelectTrigger className="w-64">
                  <SelectValue placeholder={t("select_practitioner")} />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.map((user: UserReadMinimal) => (
                    <SelectItem key={user.id} value={user.id}>
                      {formatName(user)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {queues.length > 0 && (
            <div className="ml-auto">
              <QueueFormSheet
                facilityId={facilityId}
                resourceType={resourceType}
                resourceId={effectiveResourceId}
                initialDate={startOfDay(qParams.date)}
                trigger={
                  <Button size="sm" className="font-bold" disabled={isPast}>
                    <Plus className="size-4 mr-2" />
                    {t("create_queue")}
                  </Button>
                }
              />
            </div>
          )}
        </div>

        {/* Main Content - Two Column Layout */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Queues Section - Takes up 2/3 of the width */}
          <div className="xl:col-span-2">
            {queuesLoading ? (
              <div className="space-y-3">
                <CardListSkeleton count={3} />
              </div>
            ) : queues.length === 0 ? (
              <EmptyState
                icon={<FolderOpenIcon className="text-primary size-6" />}
                title={t("no_queues_found")}
                description={t("no_queues_found_description")}
                action={
                  <QueueFormSheet
                    facilityId={facilityId}
                    resourceType={resourceType}
                    resourceId={effectiveResourceId}
                    initialDate={startOfDay(qParams.date)}
                    trigger={
                      <Button disabled={isPast}>
                        <Plus className="size-4 mr-2" />
                        {t("create_first_queue")}
                      </Button>
                    }
                  />
                }
              />
            ) : (
              <div className="overflow-hidden">
                <Table className="p-0">
                  <TableHeader className="bg-gray-100">
                    <TableRow className="rounded-md">
                      <TableHead className="w-12 border-r border-gray-200 text-gray-700">
                        #
                      </TableHead>
                      <TableHead className="border-r border-gray-200 text-gray-700">
                        {t("queue_title")}
                      </TableHead>
                      <TableHead className="w-16 border-r border-gray-200 text-gray-700">
                        {t("status")}
                      </TableHead>
                      <TableHead className="w-12 border-r border-gray-200 text-gray-700">
                        {t("settings")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {queues.map((queue, index) => (
                      <QueueRow
                        key={queue.id}
                        queue={queue}
                        facilityId={facilityId}
                        resourceType={resourceType}
                        resourceId={effectiveResourceId}
                        index={index}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          {/* Service Points Section - Takes up 1/3 of the width */}
          <div className="bg-white rounded-lg border border-gray-200 p-2 shadow">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div>
                  <div className="text-base font-medium text-gray-900">
                    {t("service_points")} - {subQueues.length}
                  </div>
                </div>
              </div>
              {subQueues.length > 0 && (
                <div className="flex items-center gap-2">
                  {!showEdit ? (
                    <TooltipComponent side="top" content={t("edit")}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setShowEdit(true)}
                      >
                        <Edit className="size-4" />
                      </Button>
                    </TooltipComponent>
                  ) : (
                    <TooltipComponent side="top" content={t("view")}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setShowEdit(false)}
                      >
                        <CareIcon icon={"l-eye"} className="text-lg" />
                      </Button>
                    </TooltipComponent>
                  )}
                  <SubQueueFormSheet
                    facilityId={facilityId}
                    resourceType={resourceType}
                    resourceId={effectiveResourceId}
                    trigger={
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <Plus className="size-4" />
                      </Button>
                    }
                  />
                </div>
              )}
            </div>

            {subQueuesLoading ? (
              <div className="space-y-3">
                <CardListSkeleton count={4} />
              </div>
            ) : subQueues.length === 0 ? (
              <EmptyState
                icon={
                  <CareIcon icon="l-map-pin" className="text-primary size-6" />
                }
                title={t("no_service_points_found")}
                description={t("no_service_points_found_description")}
                action={
                  <SubQueueFormSheet
                    facilityId={facilityId}
                    resourceType={resourceType}
                    resourceId={effectiveResourceId}
                    trigger={
                      <Button>
                        <Plus className="size-4 mr-2" />
                        {t("add_first_service_point")}
                      </Button>
                    }
                  />
                }
              />
            ) : (
              <div className="space-y-3">
                {subQueues.map((subQueue) => (
                  <SubQueueCard
                    key={subQueue.id}
                    facilityId={facilityId}
                    subQueue={subQueue}
                    showEditForAllCards={showEdit}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Pagination for queues */}
        {queuesResponse && queuesResponse.count > resultsPerPage && (
          <div className="mt-8 flex justify-center">
            <Pagination totalCount={queuesResponse.count} />
          </div>
        )}
      </div>
    </Page>
  );
}
